import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const ServicesOverview = () => {
  const coreServices = [
    {
      title: 'AI & Machine Learning',
      link: '/services/ai-machine-learning',
      description: 'Implement intelligent automation, predictive analytics, and custom AI models to transform your business operations.',
      icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      features: ['Predictive Analytics', 'Natural Language Processing', 'Computer Vision', 'Deep Learning Models']
    },
    {
      title: 'Custom Software Development',
      link: '/services/custom-software',
      description: 'Enterprise-grade bespoke applications built to your exact specifications with full integration capabilities and ongoing support.',
      icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
      features: ['Web Applications', 'Mobile Apps', 'Enterprise Software', 'API Development']
    },
    {
      title: 'Data Analytics & BI',
      link: '/services/data-analytics',
      description: 'Transform raw data into actionable business insights with advanced analytics platforms and custom visualization dashboards.',
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      features: ['Business Intelligence', 'Data Visualization', 'Real-time Analytics', 'Reporting Solutions']
    },
    {
      title: 'Cloud Implementation',
      link: '/services/cloud-implementation',
      description: 'Seamless migration to secure, scalable cloud infrastructure with enhanced performance and cost optimization strategies.',
      icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z',
      features: ['Cloud Migration', 'Infrastructure as Code', 'Multi-Cloud Strategy', 'Cloud Security']
    },
    {
      title: 'Digital Transformation',
      link: '/services/digital-transformation',
      description: 'Complete business modernization strategies leveraging cutting-edge technologies to drive innovation and growth.',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z',
      features: ['Process Digitization', 'Legacy Modernization', 'Change Management', 'Innovation Strategy']
    },
    {
      title: 'Cybersecurity',
      link: '/services/cybersecurity',
      description: 'Protect your digital assets with enterprise-grade security solutions, threat detection, and compliance management.',
      icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
      features: ['Security Audits', 'Threat Detection', 'Compliance Management', 'Security Training']
    }
  ];

  const additionalServices = [
    {
      title: 'Prompt Engineering',
      link: '/services/prompt-engineering',
      description: 'Optimize AI interactions with expertly designed prompts for maximum efficiency and accuracy.',
      icon: 'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
    },
    {
      title: 'Strategic Consultation',
      link: '/services/strategic-consultation',
      description: 'Expert guidance to identify optimal technology solutions and create comprehensive digital transformation roadmaps.',
      icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
    },
    {
      title: 'System Integration',
      link: '/services/system-integration',
      description: 'Seamless third-party application integration for unified business ecosystems.',
      icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
    },
    {
      title: 'Process Automation',
      link: '/services/process-automation',
      description: 'Streamline operations and reduce costs through intelligent workflow automation.',
      icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
    },
    {
      title: 'Managed IT Services',
      link: '/services/managed-it-services',
      description: '24/7 IT infrastructure management and support for optimal business continuity.',
      icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01'
    },
    {
      title: 'Quality Assurance',
      link: '/services/quality-assurance',
      description: 'Comprehensive testing and quality assurance to ensure flawless software delivery.',
      icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
    }
  ];

  const methodology = [
    {
      step: '01',
      title: 'Discovery & Analysis',
      description: 'We begin by understanding your business challenges, goals, and existing technology landscape through comprehensive analysis.'
    },
    {
      step: '02',
      title: 'Strategy & Planning',
      description: 'Our experts develop a tailored roadmap with clear milestones, timelines, and resource allocation for optimal results.'
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'We execute the solution using agile methodologies, ensuring continuous communication and iterative improvements.'
    },
    {
      step: '04',
      title: 'Testing & Optimization',
      description: 'Rigorous testing ensures quality and performance, followed by optimization for maximum efficiency and scalability.'
    },
    {
      step: '05',
      title: 'Deployment & Training',
      description: 'Smooth deployment with comprehensive training programs to ensure your team can leverage the solution effectively.'
    },
    {
      step: '06',
      title: 'Support & Evolution',
      description: 'Ongoing support, maintenance, and continuous improvement to adapt to your evolving business needs.'
    }
  ];

  const industries = [
    { name: 'Healthcare', link: '/solutions/healthcare-life-sciences' },
    { name: 'Financial Services', link: '/solutions/financial-services' },
    { name: 'Manufacturing', link: '/solutions/manufacturing-logistics' },
    { name: 'Retail & E-commerce', link: '/solutions/retail-ecommerce' },
    { name: 'Technology', link: '/solutions/technology-software' },
    { name: 'Government', link: '/solutions/government-public-sector' },
    { name: 'Education', link: '/solutions/education-training' },
    { name: 'Energy & Utilities', link: '/solutions/energy-utilities' }
  ];

  return (
    <>
      <SEO 
        title="Professional Services - AI, Cloud & Custom Software Solutions"
        description="Comprehensive technology services including AI & Machine Learning, custom software development, cloud implementation, cybersecurity, and digital transformation for enterprises worldwide."
        keywords="AI services, machine learning solutions, custom software development, cloud services, digital transformation, cybersecurity, IT consulting, enterprise software"
      />

      {/* Hero Section */}
      <section className="pt-20 md:pt-24 pb-12 md:pb-16 bg-pearl-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              Enterprise <span className="text-gradient">Technology Services</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up">
              Comprehensive solutions designed for enterprise-level performance and reliability. From AI and machine learning to custom software and cloud infrastructure, we deliver technology that transforms businesses.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <Link to="/contact" className="btn-primary">
                Start Your Project
              </Link>
              <Link to="/ai-hub" className="btn-secondary">
                Explore AI Solutions
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">100+</div>
              <div className="text-sm text-titanium">Global Clients</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">13</div>
              <div className="text-sm text-titanium">Service Offerings</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">12+</div>
              <div className="text-sm text-titanium">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">99%</div>
              <div className="text-sm text-titanium">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services - Modern Design */}
      <section className="py-8 bg-gradient-to-br from-alabaster via-pearl-white to-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-red rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-coral-mist rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8">
            <h2 className="section-title">Core Services</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Our flagship services that drive digital transformation and business innovation
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {coreServices.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="group relative bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-2xl border border-platinum/50"
              >
                {/* Gradient Background on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary-red/5 via-coral-mist/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {/* Animated Border */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute inset-0 border-2 border-primary-red/20 rounded-2xl"></div>
                </div>

                <div className="relative p-8">
                  {/* Icon Container with Gradient */}
                  <div className="relative mb-6">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-red to-coral-mist p-0.5 group-hover:scale-110 transition-transform duration-500">
                      <div className="w-full h-full bg-white rounded-2xl flex items-center justify-center">
                        <svg className="w-10 h-10 text-primary-red group-hover:text-deep-garnet transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={service.icon} />
                        </svg>
                      </div>
                    </div>
                    {/* Floating Badge */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-primary-red to-deep-garnet rounded-full flex items-center justify-center text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-100 scale-75">
                      {index + 1}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold mb-3 text-charcoal group-hover:text-primary-red transition-colors duration-300">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-titanium mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features with Modern Styling */}
                  <div className="space-y-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-center text-sm text-graphite group-hover:translate-x-1 transition-transform duration-300"
                        style={{ transitionDelay: `${idx * 50}ms` }}
                      >
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-primary-red/10 to-coral-mist/10 flex items-center justify-center mr-3 flex-shrink-0 group-hover:from-primary-red/20 group-hover:to-coral-mist/20 transition-colors duration-300">
                          <svg className="w-3.5 h-3.5 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="group-hover:text-charcoal transition-colors duration-300">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA with Arrow Animation */}
                  <div className="flex items-center text-primary-red font-semibold group-hover:text-deep-garnet transition-colors duration-300">
                    <span>Explore Service</span>
                    <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>

                {/* Bottom Accent Bar */}
                <div className="h-1 bg-gradient-to-r from-primary-red via-coral-mist to-primary-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </Link>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-10">
            <div className="inline-flex flex-col sm:flex-row gap-4 items-center">
              <span className="text-titanium">Need a custom solution?</span>
              <Link 
                to="/contact" 
                className="px-8 py-3 bg-gradient-to-r from-primary-red to-deep-garnet text-white rounded-lg font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300"
              >
                Talk to Our Experts
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="section-title">Additional Services</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Specialized solutions to complement your technology strategy
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalServices.map((service, index) => (
              <Link 
                key={index} 
                to={service.link}
                className="card hover:shadow-xl transition-all duration-300 group"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blush-light/60 transition-all duration-300">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={service.icon} />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2 text-charcoal group-hover:text-primary-red transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-titanium">{service.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Our Methodology */}
      <section className="py-8 bg-pearl-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="section-title">Our Methodology</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              A proven approach to delivering successful technology solutions
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {methodology.map((step, index) => (
              <div key={index} className="relative">
                <div className="card">
                  <div className="text-5xl font-bold text-coral-mist/20 mb-4">{step.step}</div>
                  <h3 className="text-xl font-semibold mb-3 text-charcoal">{step.title}</h3>
                  <p className="text-titanium">{step.description}</p>
                </div>
                {index < methodology.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <svg className="w-8 h-8 text-coral-mist/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">Why Choose Abacies</h2>
              <p className="text-titanium mb-8">
                With over 12 years of experience serving 100+ global clients, we bring unmatched expertise and commitment to every project. Our team of certified professionals combines technical excellence with deep industry knowledge.
              </p>
              <div className="space-y-6">
                {[
                  {
                    title: 'Proven Track Record',
                    description: '99% client satisfaction rate with Fortune 500 companies and innovative startups'
                  },
                  {
                    title: 'End-to-End Solutions',
                    description: 'From consultation to deployment and ongoing support, we handle everything'
                  },
                  {
                    title: 'Technology Agnostic',
                    description: 'We recommend and implement the best tools for your specific needs'
                  },
                  {
                    title: 'Scalable Architecture',
                    description: 'Solutions designed to grow with your business and adapt to changing needs'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-primary-red rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-charcoal">{item.title}</h3>
                      <p className="text-titanium">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="card text-center bg-alabaster">
                <div className="text-3xl font-bold text-primary-red mb-2">ISO 27001</div>
                <div className="text-sm text-titanium">Certified</div>
              </div>
              <div className="card text-center bg-alabaster">
                <div className="text-3xl font-bold text-primary-red mb-2">SOC 2</div>
                <div className="text-sm text-titanium">Compliant</div>
              </div>
              <div className="card text-center bg-alabaster">
                <div className="text-3xl font-bold text-primary-red mb-2">GDPR</div>
                <div className="text-sm text-titanium">Ready</div>
              </div>
              <div className="card text-center bg-alabaster">
                <div className="text-3xl font-bold text-primary-red mb-2">24/7</div>
                <div className="text-sm text-titanium">Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-8 bg-alabaster">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="section-title">Industries We Serve</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Tailored solutions for diverse industry verticals with specialized expertise
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {industries.map((industry, index) => (
              <Link 
                key={index}
                to={industry.link}
                className="card text-center hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105"
              >
                <h3 className="font-semibold text-charcoal hover:text-primary-red transition-colors">
                  {industry.name}
                </h3>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/solutions" className="btn-primary">
              Explore All Industries
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 bg-primary-red text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Let's discuss how our services can help you achieve your digital transformation goals. Schedule a free consultation with our experts today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Get Started
            </Link>
            <Link to="/company/our-story" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              Learn About Us
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="section-title">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-6">
              {[
                {
                  question: 'How do I choose the right service for my business?',
                  answer: 'We begin with a comprehensive consultation to understand your business challenges, goals, and current technology landscape. Our experts will recommend the most suitable services based on your specific needs and budget.'
                },
                {
                  question: 'What is your typical project timeline?',
                  answer: 'Project timelines vary based on scope and complexity. Simple integrations may take 4-8 weeks, while complex AI implementations can take 3-6 months. We provide detailed timelines during the planning phase.'
                },
                {
                  question: 'Do you provide ongoing support after implementation?',
                  answer: 'Yes, we offer comprehensive support packages including 24/7 monitoring, regular updates, troubleshooting, and continuous optimization to ensure your solution remains effective and up-to-date.'
                },
                {
                  question: 'Can you work with our existing technology stack?',
                  answer: 'Absolutely. We specialize in integrating with existing systems and are technology-agnostic. We assess your current infrastructure and recommend the best approach for seamless integration.'
                }
              ].map((faq, index) => (
                <div key={index} className="card">
                  <h3 className="text-lg font-semibold mb-3 text-charcoal">{faq.question}</h3>
                  <p className="text-titanium">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesOverview;